import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import PrintModal from '../../../components/PrintModal';
import { API_DOCS } from './CapitolatiListaPage.api';
import './CapitolatiListaPage.css';

// ── Icone ──────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const PrinterIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ── Tipi ───────────────────────────────────────────────────────────────────

interface SedeOption {
  id: number;
  nome: string;
}

interface Capitolato {
  id: number;
  codice: string;
  nome: string;
  descrizione: string | null;
  attivo: boolean;
  sedeId: number | null;
  createdAt: string;
  createdBy: { id: number; nome: string } | null;
  sede: { id: number; nome: string } | null;
  _count: { righePrecConfigurate: number };
}

interface CapitolatoDettaglio extends Omit<Capitolato, '_count'> {
  righePrecConfigurate: RigaCapitolato[];
  righeAccessorie: RigaAccessoria[];
  _count: { righePrecConfigurate: number };
}

interface RigaCapitolato {
  id: number;
  ordine: number;
  prezzoUnitario: string;
  descrizioneRiga: string | null;
  articoloPreconfig: {
    id: number;
    codice: string;
    nome: string;
  };
}

/** PRODOTTO_CONFIGURATO → "Prodotto configurato" (va a capo e non allarga la tabella) */
function etichettaTipologia(t: string | null | undefined): string {
  if (!t) return '—';
  const testo = t.split('_').join(' ').toLowerCase();
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

interface RigaAccessoria {
  id: number;
  ordine: number;
  prezzoUnitario: string;
  descrizioneRiga: string | null;
  articolo: {
    id: number;
    codice: string;
    descrizioneBase: string;
    tipologia: string;
    umVendita: { simbolo: string };
  };
}

interface RigaLocale {
  _key: string;
  id?: number;
  articoloPreconfigId: number;
  articoloCodice: string;
  articoloNome: string;
  ordine: number;
  prezzoUnitario: number;
  descrizioneRiga: string;
}

interface RigaAccessoriaLocale {
  _key: string;
  id?: number;
  articoloId: number;
  articoloCodice: string;
  articoloNome: string;
  articoloTipologia: string;
  articoloUm: string;
  ordine: number;
  prezzoUnitario: number;
  descrizioneRiga: string;
}

interface ArticoloPreconfigOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ArticoloOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita: { simbolo: string };
}

// ── Modale selezione articolo preconfigurato ───────────────────────────────

function ModalRicercaPreconf({
  societaId,
  onClose,
  onSeleziona,
}: {
  societaId: string;
  onClose: () => void;
  onSeleziona: (a: ArticoloPreconfigOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloPreconfigOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaId) params.set('societaId', societaId);
      if (q.trim()) params.set('q', q.trim());
      // @api GET /api/articoli-preconfigurati?societaId={id}&q={q} -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","nComponenti":"number"}]}
      const res = await apiFetch<{ success: boolean; data: ArticoloPreconfigOption[] }>(
        `/api/articoli-preconfigurati?${params}`
      );
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => {
    cerca('');
  }, [cerca]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  return (
    <div className="cap-modal">
      <div className="cap-modal__box cap-modal__box--medium">
        <div className="cap-modal__header">
          <div>
            <h2 className="cap-modal__title">Seleziona Articolo Pre-configurato</h2>
          </div>
          <button className="cap-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="cap-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca articolo</label>
            <input
              className="bop-input"
              value={query}
              onChange={handleChange}
              placeholder="Cerca per codice o nome..."
              autoFocus
            />
          </div>
          {loading && (
            <div className="bop-empty-state"><span className="bop-text-muted">Caricamento...</span></div>
          )}
          {!loading && results.length === 0 && (
            <div className="bop-empty-state">Nessun articolo trovato</div>
          )}
          {!loading && results.length > 0 && (
            <div className="cap-search-dropdown">
              {results.map(a => (
                <div
                  key={a.id}
                  className="cap-search-item"
                  onClick={() => onSeleziona(a)}
                >
                  <div className="cap-search-item__codice">{a.codice} — {a.nome}</div>
                  <div className="cap-search-item__meta">
                    Padre: {a.articoloPadreCodice} | UM: {a.umVenditaSimbolo} | Componenti: {a.nComponenti}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cap-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale selezione articolo da anagrafica ───────────────────────────────

function ModalRicercaArticolo({
  societaId,
  onClose,
  onSeleziona,
}: {
  societaId: string;
  onClose: () => void;
  onSeleziona: (a: ArticoloOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaId) params.set('societaId', societaId);
      if (q.trim()) params.set('q', q.trim());
      // @api GET /api/anagrafiche/articoli?societaId={id}&q={q}&limit=50 -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string","umVendita":{"simbolo":"string"}}],"total":"number"}
      const res = await apiFetch<{ success: boolean; data: ArticoloOption[]; total: number }>(
        `/api/anagrafiche/articoli?${params}&limit=50`
      );
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { cerca(''); }, [cerca]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  return (
    <div className="cap-modal">
      <div className="cap-modal__box cap-modal__box--medium">
        <div className="cap-modal__header">
          <div>
            <h2 className="cap-modal__title">Seleziona Articolo</h2>
            <p className="cap-modal__subtitle">Anagrafica articoli completa</p>
          </div>
          <button className="cap-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="cap-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca articolo</label>
            <input
              className="bop-input"
              value={query}
              onChange={handleChange}
              placeholder="Cerca per codice o descrizione..."
              autoFocus
            />
          </div>
          {loading && <div className="bop-empty-state"><span className="bop-text-muted">Caricamento...</span></div>}
          {!loading && results.length === 0 && <div className="bop-empty-state">Nessun articolo trovato</div>}
          {!loading && results.length > 0 && (
            <div className="cap-search-dropdown">
              {results.map(a => (
                <div key={a.id} className="cap-search-item" onClick={() => onSeleziona(a)}>
                  <div className="cap-search-item__codice">{a.codice} — {a.descrizioneBase}</div>
                  <div className="cap-search-item__meta">
                    Tipologia: {a.tipologia} | UM: {a.umVendita?.simbolo || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="cap-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale modifica singola riga ───────────────────────────────────────────

function ModalRiga({
  riga,
  onClose,
  onSalva,
}: {
  riga: RigaLocale;
  onClose: () => void;
  onSalva: (r: RigaLocale) => void;
}) {
  const [prezzo, setPrezzo] = useState(String(riga.prezzoUnitario));
  const [descr, setDescr] = useState(riga.descrizioneRiga);

  const handleSalva = () => {
    const p = parseFloat(prezzo);
    if (isNaN(p) || p < 0) {
      showToast('Prezzo non valido', 'error');
      return;
    }
    onSalva({ ...riga, prezzoUnitario: p, descrizioneRiga: descr });
  };

  return (
    <div className="cap-modal">
      <div className="cap-modal__box cap-modal__box--medium">
        <div className="cap-modal__header">
          <h2 className="cap-modal__title">Modifica Riga</h2>
          <button className="cap-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="cap-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Articolo</label>
            <div className="cap-riga-info">
              <strong>{riga.articoloCodice}</strong> — {riga.articoloNome}
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Prezzo unitario *</label>
            <input
              className="bop-input"
              type="number"
              step="0.01"
              min="0"
              value={prezzo}
              onChange={e => setPrezzo(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione riga</label>
            <input
              className="bop-input"
              value={descr}
              onChange={e => setDescr(e.target.value)}
              placeholder="Descrizione opzionale..."
            />
          </div>
        </div>
        <div className="cap-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSalva}>Salva</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale creazione / modifica capitolato ─────────────────────────────────

function ModalCapitolato({
  capitolato,
  societaId,
  onClose,
  onSalvato,
}: {
  capitolato: CapitolatoDettaglio | null;
  societaId: string;
  onClose: () => void;
  onSalvato: () => void;
}) {
  const isModifica = capitolato !== null;
  const [nome, setNome] = useState(capitolato?.nome || '');
  const [descrizione, setDescrizione] = useState(capitolato?.descrizione || '');
  const [attivo, setAttivo] = useState(capitolato ? capitolato.attivo : true);
  const [sedeIdMod, setSedeIdMod] = useState<string>(capitolato?.sedeId ? String(capitolato.sedeId) : '');
  const [sediDisponibili, setSediDisponibili] = useState<SedeOption[]>([]);

  useEffect(() => {
    if (!societaId) return;
    // @api GET /api/sedi/mie?societaId={id}&commerciale=true -> {"success":true,"data":[{"id":"number","nome":"string"}]}
    apiFetch<{ success: boolean; data: SedeOption[] }>(`/api/sedi/mie?societaId=${societaId}&commerciale=true`)
      .then(res => setSediDisponibili(res.data || []))
      .catch(() => setSediDisponibili([]));
  }, [societaId]);

  const [righePrecConfigurate, setRighePrecConfigurate] = useState<RigaLocale[]>(
    capitolato?.righePrecConfigurate.map((r, idx) => ({
      _key: String(r.id || idx),
      id: r.id,
      articoloPreconfigId: r.articoloPreconfig.id,
      articoloCodice: r.articoloPreconfig.codice,
      articoloNome: r.articoloPreconfig.nome,
      ordine: r.ordine,
      prezzoUnitario: parseFloat(r.prezzoUnitario),
      descrizioneRiga: r.descrizioneRiga || '',
    })) || []
  );

  const [righeAccessorie, setRigheAccessorie] = useState<RigaAccessoriaLocale[]>(
    capitolato?.righeAccessorie.map((r, idx) => ({
      _key: String(r.id || idx),
      id: r.id,
      articoloId: r.articolo.id,
      articoloCodice: r.articolo.codice,
      articoloNome: r.articolo.descrizioneBase,
      articoloTipologia: r.articolo.tipologia,
      articoloUm: r.articolo.umVendita?.simbolo || '',
      ordine: r.ordine,
      prezzoUnitario: parseFloat(r.prezzoUnitario),
      descrizioneRiga: r.descrizioneRiga || '',
    })) || []
  );

  const [loading, setSaving] = useState(false);
  const [showRicercaPreconf, setShowRicercaPreconf] = useState(false);
  const [showRicercaArticolo, setShowRicercaArticolo] = useState(false);
  const [rigaInModifica, setRigaInModifica] = useState<RigaLocale | null>(null);
  const [rigaAccessoriaInModifica, setRigaAccessoriaInModifica] = useState<RigaAccessoriaLocale | null>(null);

  const handleAggiungiArticolo = (a: ArticoloPreconfigOption) => {
    const nuovaRiga: RigaLocale = {
      _key: `new-${Date.now()}-${Math.random()}`,
      articoloPreconfigId: a.id,
      articoloCodice: a.codice,
      articoloNome: a.nome,
      ordine: righePrecConfigurate.length,
      prezzoUnitario: 0,
      descrizioneRiga: '',
    };
    setRighePrecConfigurate(prev => [...prev, nuovaRiga]);
    setShowRicercaPreconf(false);
  };

  const handleRimuoviRiga = (_key: string) => {
    setRighePrecConfigurate(prev => prev.filter(r => r._key !== _key).map((r, idx) => ({ ...r, ordine: idx })));
  };

  const handleModificaRiga = (r: RigaLocale) => {
    setRigaInModifica(r);
  };

  const handleSalvaRiga = (r: RigaLocale) => {
    setRighePrecConfigurate(prev => prev.map(x => x._key === r._key ? r : x));
    setRigaInModifica(null);
  };

  const handleAggiungiAccessorio = (a: ArticoloOption) => {
    const nuovaRiga: RigaAccessoriaLocale = {
      _key: `acc-${Date.now()}-${Math.random()}`,
      articoloId: a.id,
      articoloCodice: a.codice,
      articoloNome: a.descrizioneBase,
      articoloTipologia: a.tipologia,
      articoloUm: a.umVendita?.simbolo || '',
      ordine: righeAccessorie.length,
      prezzoUnitario: 0,
      descrizioneRiga: '',
    };
    setRigheAccessorie(prev => [...prev, nuovaRiga]);
    setShowRicercaArticolo(false);
  };

  const handleRimuoviAccessorio = (_key: string) => {
    setRigheAccessorie(prev => prev.filter(r => r._key !== _key).map((r, idx) => ({ ...r, ordine: idx })));
  };

  const handleSalvaAccessorio = (r: RigaAccessoriaLocale) => {
    setRigheAccessorie(prev => prev.map(x => x._key === r._key ? r : x));
    setRigaAccessoriaInModifica(null);
  };

  const handleSalva = async () => {
    if (!nome.trim()) {
      showToast('Il nome è obbligatorio', 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        societaId: parseInt(societaId, 10),
        nome: nome.trim(),
        descrizione: descrizione.trim() || null,
        attivo,
        sedeId: sedeIdMod ? parseInt(sedeIdMod, 10) : null,
        righePrecConfigurate: righePrecConfigurate.map((r, idx) => ({
          articoloPreconfigId: r.articoloPreconfigId,
          ordine: idx,
          prezzoUnitario: r.prezzoUnitario,
          descrizioneRiga: r.descrizioneRiga || null,
        })),
        righeAccessorie: righeAccessorie.map((r, idx) => ({
          articoloId: r.articoloId,
          ordine: idx,
          prezzoUnitario: r.prezzoUnitario,
          descrizioneRiga: r.descrizioneRiga || null,
        })),
      };

      if (isModifica) {
        // @api PUT /api/capitolati/:id {"nome":"string","descrizione":"string","attivo":"boolean","righe":[]} -> {"success":true,"data":{}}
        await apiFetch(`/api/capitolati/${capitolato!.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        showToast('Capitolato aggiornato', 'success');
      } else {
        // @api POST /api/capitolati {"societaId":"number","nome":"string","descrizione":"string","attivo":"boolean","righe":[]} -> {"success":true,"data":{}}
        await apiFetch('/api/capitolati', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        showToast('Capitolato creato', 'success');
      }
      onSalvato();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore nel salvataggio';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="cap-modal">
        <div className="cap-modal__box cap-modal__box--large">
          <div className="cap-modal__header">
            <div>
              <h2 className="cap-modal__title">
                {isModifica ? `Modifica — ${capitolato!.codice}` : 'Nuovo Capitolato'}
              </h2>
              {isModifica && capitolato!.createdBy && (
                <p className="cap-modal__subtitle">
                  Creato da {capitolato!.createdBy.nome} il {new Date(capitolato!.createdAt).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
            <button className="cap-modal__close" onClick={onClose}><XIcon /></button>
          </div>

          <div className="cap-modal__body">
            {/* Dati capitolato */}
            <div className="cap-form-grid">
              <div className="bop-form-group">
                <label className="bop-label">Nome *</label>
                <input
                  className="bop-input"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Nome capitolato..."
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Stato</label>
                <select
                  className="bop-select"
                  value={attivo ? 'true' : 'false'}
                  onChange={e => setAttivo(e.target.value === 'true')}
                >
                  <option value="true">Attivo</option>
                  <option value="false">Non attivo</option>
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Sede</label>
                <select
                  className="bop-select"
                  value={sedeIdMod}
                  onChange={e => setSedeIdMod(e.target.value)}
                >
                  <option value="">— Nessuna sede —</option>
                  {sediDisponibili.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
              <div className={`bop-form-group cap-form-grid--full`}>
                <label className="bop-label">Descrizione</label>
                <textarea
                  className="bop-input"
                  rows={2}
                  value={descrizione}
                  onChange={e => setDescrizione(e.target.value)}
                  placeholder="Descrizione opzionale..."
                />
              </div>
            </div>

            {/* Righe pre-configurate */}
            <div className="cap-section-header">
              <div className="cap-section-header__left">
                <span className="cap-section-label">Righe articoli ({righePrecConfigurate.length})</span>
                <span className="cap-section-tooltip" title="Queste righe accettano solo articoli pre-configurati. Un articolo pre-configurato è una combinazione predefinita di componenti con prezzo definito.">
                  &#9432;
                </span>
              </div>
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => setShowRicercaPreconf(true)}
              >
                <PlusIcon /> Aggiungi articolo
              </button>
            </div>

            {righePrecConfigurate.length === 0 ? (
              <div className="cap-righe-empty">
                Nessuna riga. Usa "Aggiungi articolo" per aggiungere articoli pre-configurati.
              </div>
            ) : (
              <div className="bop-card">
                <div className="bop-capitolato__righe-scroll">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Codice</th>
                      <th>Nome</th>
                      <th>Prezzo unitario</th>
                      <th>Descrizione riga</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righePrecConfigurate.map((r, idx) => (
                      <tr key={r._key}>
                        <td>{idx + 1}</td>
                        <td><span className="cap-badge-codice">{r.articoloCodice}</span></td>
                        <td>{r.articoloNome}</td>
                        <td>€ {r.prezzoUnitario.toFixed(2)}</td>
                        <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                        <td>
                          <div className="bop-flex">
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Modifica"
                              onClick={() => handleModificaRiga(r)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Rimuovi"
                              onClick={() => handleRimuoviRiga(r._key)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* Sezione articoli accessori */}
            <div className="cap-section-header cap-section-header--secondary">
              <div className="cap-section-header__left">
                <span className="cap-section-label">Articoli accessori aggiuntivi ({righeAccessorie.length})</span>
                <span className="cap-section-tooltip" title="Articoli accessori liberamente selezionabili dall'intera anagrafica articoli. Non richiedono una pre-configurazione.">
                  &#9432;
                </span>
              </div>
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => setShowRicercaArticolo(true)}
              >
                <PlusIcon /> Aggiungi accessorio
              </button>
            </div>

            {righeAccessorie.length === 0 ? (
              <div className="cap-righe-empty">
                Nessun articolo accessorio. Usa "Aggiungi accessorio" per aggiungere articoli dall'anagrafica.
              </div>
            ) : (
              <div className="bop-card">
                <div className="bop-capitolato__righe-scroll">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Codice</th>
                      <th>Nome</th>
                      <th>Tipologia</th>
                      <th>UM</th>
                      <th>Prezzo unitario</th>
                      <th>Descrizione riga</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righeAccessorie.map((r, idx) => (
                      <tr key={r._key}>
                        <td>{idx + 1}</td>
                        <td><span className="cap-badge-codice cap-badge-codice--accessorio">{r.articoloCodice}</span></td>
                        <td>{r.articoloNome}</td>
                        <td><span className="bop-text-muted">{etichettaTipologia(r.articoloTipologia)}</span></td>
                        <td>{r.articoloUm}</td>
                        <td>€ {r.prezzoUnitario.toFixed(2)}</td>
                        <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                        <td>
                          <div className="bop-flex">
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Modifica"
                              onClick={() => setRigaAccessoriaInModifica(r)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Rimuovi"
                              onClick={() => handleRimuoviAccessorio(r._key)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>

          <div className="cap-modal__footer">
            <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={loading}>
              Annulla
            </button>
            <button className="bop-btn bop-btn-primary" onClick={handleSalva} disabled={loading}>
              {loading ? 'Salvataggio...' : isModifica ? 'Salva modifiche' : 'Crea capitolato'}
            </button>
          </div>
        </div>
      </div>

      {showRicercaPreconf && (
        <ModalRicercaPreconf
          societaId={societaId}
          onClose={() => setShowRicercaPreconf(false)}
          onSeleziona={handleAggiungiArticolo}
        />
      )}

      {rigaInModifica && (
        <ModalRiga
          riga={rigaInModifica}
          onClose={() => setRigaInModifica(null)}
          onSalva={handleSalvaRiga}
        />
      )}

      {showRicercaArticolo && (
        <ModalRicercaArticolo
          societaId={societaId}
          onClose={() => setShowRicercaArticolo(false)}
          onSeleziona={handleAggiungiAccessorio}
        />
      )}

      {rigaAccessoriaInModifica && (
        <ModalRiga
          riga={{
            _key: rigaAccessoriaInModifica._key,
            id: rigaAccessoriaInModifica.id,
            articoloPreconfigId: rigaAccessoriaInModifica.articoloId,
            articoloCodice: rigaAccessoriaInModifica.articoloCodice,
            articoloNome: rigaAccessoriaInModifica.articoloNome,
            ordine: rigaAccessoriaInModifica.ordine,
            prezzoUnitario: rigaAccessoriaInModifica.prezzoUnitario,
            descrizioneRiga: rigaAccessoriaInModifica.descrizioneRiga,
          }}
          onClose={() => setRigaAccessoriaInModifica(null)}
          onSalva={(r) => handleSalvaAccessorio({
            ...rigaAccessoriaInModifica,
            prezzoUnitario: r.prezzoUnitario,
            descrizioneRiga: r.descrizioneRiga,
          })}
        />
      )}
    </>
  );
}

// ── Componente principale ──────────────────────────────────────────────────

export default function CapitolatiListaPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can('commerciale.capitolati', 'edit');
  const canDelete = can('commerciale.capitolati', 'delete');

  const [capitolati, setCapitolati] = useState<Capitolato[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [filtroAttivo, setFiltroAttivo] = useState<'tutti' | 'true' | 'false'>('tutti');
  const [filtroSedeId, setFiltroSedeId] = useState('');
  const [sediLista, setSediLista] = useState<SedeOption[]>([]);
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const [modalAperto, setModalAperto] = useState(false);
  const [capitolatoDettaglio, setCapitolatoDettaglio] = useState<CapitolatoDettaglio | null>(null);
  const [idModifica, setIdModifica] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Capitolato | null>(null);
  const [printTarget, setPrintTarget] = useState<{ id: number; label: string; societaId: number } | null>(null);

  // Determina societaId effettivo
  const societaIdEff = societaId || societaIdLocale;

  // Carica sedi quando cambia la società
  useEffect(() => {
    if (!societaIdEff) { setSediLista([]); return; }
    // @api GET /api/sedi/mie?societaId={id}&commerciale=true -> {"success":true,"data":[{"id":"number","nome":"string"}]}
    apiFetch<{ success: boolean; data: SedeOption[] }>(`/api/sedi/mie?societaId=${societaIdEff}&commerciale=true`)
      .then(res => setSediLista(res.data || []))
      .catch(() => setSediLista([]));
  }, [societaIdEff]);

  const carica = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (filtroSedeId) params.set('sedeId', filtroSedeId);
      if (q.trim()) params.set('q', q.trim());
      if (filtroAttivo !== 'tutti') params.set('attivo', filtroAttivo);
      // @api GET /api/capitolati?societaId={id}&sedeId={sedeId}&q={q}&attivo={attivo} -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","attivo":"boolean","sedeId":"number","sede":{"id":"number","nome":"string"},"_count":{"righe":"number"}}]}
      const res = await apiFetch<{ success: boolean; data: Capitolato[] }>(
        `/api/capitolati?${params}`
      );
      setCapitolati(res.data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, q, filtroAttivo, filtroSedeId]);

  useEffect(() => {
    carica();
  }, [carica]);

  const apriNuovo = () => {
    setCapitolatoDettaglio(null);
    setIdModifica(null);
    setModalAperto(true);
  };

  const apriModifica = async (cap: Capitolato) => {
    try {
      // @api GET /api/capitolati/:id -> {"success":true,"data":{"id":"number","codice":"string","nome":"string","righe":[]}}
      const res = await apiFetch<{ success: boolean; data: CapitolatoDettaglio }>(
        `/api/capitolati/${cap.id}`
      );
      setCapitolatoDettaglio(res.data);
      setIdModifica(cap.id);
      setModalAperto(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore nel caricamento';
      showToast(msg, 'error');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      // @api DELETE /api/capitolati/:id -> {"success":true}
      await apiFetch(`/api/capitolati/${confirmDelete.id}`, { method: 'DELETE' });
      showToast('Capitolato eliminato', 'success');
      setConfirmDelete(null);
      carica();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore nella eliminazione';
      showToast(msg, 'error');
    }
  };

  const societaEffEff = societaIdEff;

  return (
    <Layout>
      <div className="cap-page">
        {/* Header pagina */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Capitolati</h1>
          {canEdit && pronta && (
            <button className="bop-btn bop-btn-primary" onClick={apriNuovo}>
              <PlusIcon /> Nuovo capitolato
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-card cap-filtri">
          <div className="cap-filtri__row">
            {!isDisabled && (
              <div className="bop-form-group">
                <label className="bop-label">Società</label>
                <select
                  className="bop-select"
                  value={societaIdLocale}
                  onChange={e => setSocietaIdLocale(e.target.value)}
                >
                  {isSuperadmin && <option value="">Tutte</option>}
                  {listaSocieta.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">Cerca</label>
              <input
                className="bop-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Codice o nome..."
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Stato</label>
              <select
                className="bop-select"
                value={filtroAttivo}
                onChange={e => setFiltroAttivo(e.target.value as 'tutti' | 'true' | 'false')}
              >
                <option value="tutti">Tutti</option>
                <option value="true">Attivi</option>
                <option value="false">Non attivi</option>
              </select>
            </div>
            {sediLista.length > 0 && (
              <div className="bop-form-group">
                <label className="bop-label">Sede</label>
                <select
                  className="bop-select"
                  value={filtroSedeId}
                  onChange={e => setFiltroSedeId(e.target.value)}
                >
                  <option value="">Tutte</option>
                  {sediLista.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">&nbsp;</label>
              <button className="bop-btn bop-btn-secondary" onClick={carica}>
                <SearchIcon /> Cerca
              </button>
            </div>
          </div>
        </div>

        {/* Tabella */}
        <div className="bop-card">
          {!pronta ? (
            <div className="bop-empty-state">Seleziona una società per visualizzare i capitolati</div>
          ) : loading ? (
            <div className="bop-empty-state">Caricamento...</div>
          ) : capitolati.length === 0 ? (
            <div className="bop-empty-state">Nessun capitolato trovato</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Nome</th>
                  <th>Descrizione</th>
                  <th>Sede</th>
                  <th>Righe</th>
                  <th>Stato</th>
                  <th>Creato da</th>
                  <th>Data</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {capitolati.map(cap => (
                  <tr key={cap.id}>
                    <td><span className="cap-badge-codice">{cap.codice}</span></td>
                    <td>{cap.nome}</td>
                    <td>{cap.descrizione ? cap.descrizione : <span className="bop-text-muted">—</span>}</td>
                    <td>{cap.sede?.nome || <span className="bop-text-muted">—</span>}</td>
                    <td><span className="cap-righe-count">{cap._count.righePrecConfigurate}</span></td>
                    <td>
                      {cap.attivo
                        ? <span className="cap-badge-attivo">Attivo</span>
                        : <span className="cap-badge-inattivo">Non attivo</span>
                      }
                    </td>
                    <td>{cap.createdBy?.nome || <span className="bop-text-muted">—</span>}</td>
                    <td>{new Date(cap.createdAt).toLocaleDateString('it-IT')}</td>
                    <td>
                      <div className="bop-flex">
                        {canEdit && (
                          <button
                            className="bop-btn bop-btn-icon"
                            title="Modifica"
                            onClick={() => apriModifica(cap)}
                          >
                            <EditIcon />
                          </button>
                        )}
                        <button
                          className="bop-btn bop-btn-icon"
                          title="Stampa / Invia"
                          onClick={() => setPrintTarget({
                            id: cap.id,
                            label: `${cap.codice} — ${cap.nome}`,
                            societaId: parseInt(societaIdEff || '0', 10),
                          })}
                        >
                          <PrinterIcon />
                        </button>
                        {canDelete && (
                          <button
                            className="bop-btn bop-btn-icon"
                            title="Elimina"
                            onClick={() => setConfirmDelete(cap)}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modale creazione/modifica */}
        {modalAperto && (
          <ModalCapitolato
            capitolato={idModifica !== null ? capitolatoDettaglio : null}
            societaId={societaEffEff}
            onClose={() => { setModalAperto(false); setCapitolatoDettaglio(null); }}
            onSalvato={() => { setModalAperto(false); setCapitolatoDettaglio(null); carica(); }}
          />
        )}

        {/* Confirm delete */}
        {confirmDelete && (
          <div className="cap-modal">
            <div className="cap-modal__box cap-modal__box--medium">
              <div className="cap-modal__header">
                <h2 className="cap-modal__title">Elimina capitolato</h2>
                <button className="cap-modal__close" onClick={() => setConfirmDelete(null)}><XIcon /></button>
              </div>
              <div className="cap-modal__body">
                <p>
                  Sei sicuro di voler eliminare il capitolato <strong>{confirmDelete.codice} — {confirmDelete.nome}</strong>?
                  Questa azione non può essere annullata.
                </p>
              </div>
              <div className="cap-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setConfirmDelete(null)}>Annulla</button>
                <button className="bop-btn bop-btn-danger" onClick={handleDelete}>Elimina</button>
              </div>
            </div>
          </div>
        )}

        {printTarget && (
          <PrintModal
            societaId={printTarget.societaId}
            entitaType="capitolato"
            entitaId={printTarget.id}
            entitaLabel={printTarget.label}
            onClose={() => setPrintTarget(null)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
